<template>
  <div id="registration-completion" class="fill-height align-start">
    <template v-if="!loading">
      <v-row class="g-container" no-gutters>
        <v-col>
          <v-card
            v-if="!supplierId || !token"
            class="i-empty-card invitation-card rounded-lg pa-4 pa-md-10"
          >
            <v-icon color="red" class="i-empty-card-icon mb-4">
              mdi-delete-outline
            </v-icon>
            <h2 class="g-text-color-primary">
              This registration completion is invalid
            </h2>
            <div class="mt-4">
              <p>
                <router-link class="i-a-link" :to="{ name: 'login' }"
                  >Go back to log in page</router-link
                >
              </p>
            </div>
          </v-card>

          <v-card
            v-else-if="registrationInvitationError"
            class="i-empty-card invitation-card rounded-lg pa-4 pa-md-10"
          >
            <v-icon color="red" class="i-empty-card-icon mb-4">
              mdi-delete-outline
            </v-icon>
            <h2 class="g-text-color-primary">
              {{ registrationInvitationError }}
            </h2>
            <div class="mt-4">
              <p>
                <router-link class="i-a-link" :to="{ name: 'login' }"
                  >Go back to log in page</router-link
                >
              </p>
            </div>
          </v-card>

          <v-card v-else class="p-invitation-card rounded-lg pa-4 pa-md-10">
            <v-card-title class="pa-0 pb-2">
              <h2 class="g-text-color-primary">Complete Registration</h2>
            </v-card-title>
            <v-divider class="mx-0" horizontal></v-divider>
            <v-form v-model="formState" :disabled="submitting" @submit.prevent>
              <span class="g-heading-s g-bold">Select new password</span>
              <v-row>
                <v-col class="pt-2" cols="12" md="6">
                  <div
                    class="p-password-field"
                    :class="{
                      'p-password-field--active':
                        passwordFocused || password || passwordFieldError,
                      'p-password-field--error': !!passwordFieldError
                    }"
                  >
                    <div class="p-password-field__control">
                      <fieldset class="p-password-field__fieldset">
                        <legend class="p-password-field__legend">
                          <span>Password</span>
                        </legend>
                      </fieldset>
                      <label class="p-password-field__label">Password</label>
                      <input
                        class="p-password-field__input"
                        autocomplete="off"
                        required
                        :type="showpassword ? 'text' : 'password'"
                        :value="password"
                        @blur="passwordFocused = false"
                        @focus="focusPassword"
                        @input="updatePassword"
                      />
                      <button
                        class="p-password-field__append mdi"
                        :class="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
                        type="button"
                        @click="showpassword = !showpassword"
                      ></button>
                    </div>
                    <div class="p-password-field__details">
                      {{ passwordFieldError }}
                    </div>
                  </div>

                  <div v-if="passwordValidator" class="mb-4 ml-2" cols="12">
                    <i-password-criteria :password="password" />
                  </div>
                </v-col>

                <v-col class="pt-2" cols="12" md="6">
                  <div
                    class="p-password-field"
                    :class="{
                      'p-password-field--active':
                        repasswordFocused || repassword || repasswordFieldError,
                      'p-password-field--error': !!repasswordFieldError
                    }"
                  >
                    <div class="p-password-field__control">
                      <fieldset class="p-password-field__fieldset">
                        <legend class="p-password-field__legend">
                          <span>Re-type Password</span>
                        </legend>
                      </fieldset>
                      <label class="p-password-field__label">
                        Re-type Password
                      </label>
                      <input
                        class="p-password-field__input"
                        autocomplete="off"
                        required
                        type="password"
                        :value="repassword"
                        @blur="repasswordFocused = false"
                        @focus="repasswordFocused = true"
                        @input="updateRetypedPassword"
                      />
                    </div>
                    <div class="p-password-field__details">
                      {{ repasswordFieldError }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-form>

            <v-card-actions class="pa-0">
              <v-spacer></v-spacer>
              <v-btn
                rounded
                large
                type="submit"
                class="i-btn-block-secondary p-btn"
                :disabled="!passwordFormState"
                :loading="submitting"
                @click="completeRegistration"
              >
                <strong>Confirm</strong>
                <v-icon class="pl-1 pt-1" small>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

import { mapActions } from 'vuex'
import PasswordHelper from '@/components/helpers/PasswordHelper'
import iPasswordCriteria from '@/components/PasswordCriteria'
import {
  completeRegistration,
  fetchRegistrationInvitation
} from '@/services/registration'

export default defineComponent({
  components: { iPasswordCriteria },

  data: () => ({
    loading: false,
    formState: false,
    password: '',
    passwordErrorMessage: null,
    passwordFocused: false,
    passwordValidator: false,
    repassword: '',
    repasswordFocused: false,
    repasswordTouched: false,
    showpassword: false,
    submitting: false,
    supplierId: null,
    token: null,
    registrationInvitation: null,
    registrationInvitationError: null
  }),

  computed: {
    passwordsMatchRule() {
      return PasswordHelper.passwordsMatchRule(this.password, this.repassword)
    },

    passwordRules() {
      return PasswordHelper.passwordRules()
    },

    passwordRuleError() {
      for (const rule of this.passwordRules) {
        const result = rule(this.password)
        if (result !== true) return result
      }
      return ''
    },

    passwordFieldError() {
      return this.passwordErrorMessage || this.passwordRuleError
    },

    repasswordFieldError() {
      if (!this.repasswordTouched) return ''
      return this.passwordsMatchRule === true ? '' : this.passwordsMatchRule
    },

    passwordFormState() {
      return (
        !!this.password &&
        !!this.repassword &&
        !this.passwordRuleError &&
        this.passwordsMatchRule === true
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    readUrlParams() {
      this.supplierId = this.$route.query.supplierId
      this.token = this.$route.query.token
    },

    fetchRegistrationInvitation() {
      if (this.supplierId && this.token) {
        this.loading = true
        fetchRegistrationInvitation(this.supplierId, this.token)
          .then((response) => {
            this.registrationInvitation = response
          })
          .catch((err) => {
            let error = null
            switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
              case 'INVITATION_NOT_VALID':
                error = 'This registration completion is not valid'
                break

              case 'INVITATION_NO_LONGER_PENDING':
                error = 'This registration completion is no longer pending'
                break

              case 'INVITATION_TOKEN_NO_LONGER_VALID':
                error = 'This registration completion token is no longer valid'
                break

              default:
                error =
                  'Registration could not be loaded. Please contact support...'
            }
            this.registrationInvitationError = error
          })
          .finally(() => {
            this.loading = false
          })
      }
    },

    resolveInputValue(value) {
      return value?.target ? value.target.value : value
    },

    updatePassword(value) {
      this.password = this.resolveInputValue(value)
      this.passwordErrorMessage = null
    },

    updateRetypedPassword(value) {
      this.repassword = this.resolveInputValue(value)
      this.repasswordTouched = true
    },

    focusPassword() {
      this.passwordFocused = true
      this.passwordValidator = true
    },

    completeRegistration() {
      if (!this.passwordFormState) return
      this.submitting = true
      completeRegistration(this.supplierId, this.token, this.password)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Registration successfully completed!'
          })
          this.$router.push({ name: 'login' })
        })
        .catch((err) => {
          let error = null
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            case 'PASSWORD_TOO_WEAK':
              error = this.passwordErrorMessage = 'Password is too weak'
              break

            case 'PASSWORD_TOO_COMMON':
              error = this.passwordErrorMessage = 'Password is too common'
              break

            case 'PASSWORD_CONTAINS_USER_INFO':
              error = this.passwordErrorMessage =
                'Password contains some of your information'
              break

            case 'EMAIL_EXISTS':
              error = 'This user email is already registered on our system'
              break

            case 'INVITATION_NOT_VALID':
              error = 'This registration completion is no longer valid'
              break

            case 'INVITATION_NO_LONGER_PENDING':
              error = 'This registration completion is no longer pending'
              break

            case 'INVITATION_TOKEN_NO_LONGER_VALID':
              error = 'This registration completion token is no longer valid'
              break

            default:
              error =
                'Registration could not be completed. Please contact support...'
          }
          this.addNotification({
            color: 'error',
            text: error
          })
        })
        .finally(() => {
          this.submitting = false
        })
    }
  },

  mounted() {
    this.readUrlParams()
    this.fetchRegistrationInvitation()
  }
})
</script>

<style lang="scss" scoped>
#registration-completion {
  hr {
    min-width: 100%;
    margin-top: 15px;
    margin-bottom: 20px;
    border-color: rgba(0, 0, 0, 0.12);
  }

  .p-invitation-card {
    max-width: 740px;
    margin: auto;
  }

  .p-btn {
    width: 100%;
  }

  margin: auto;
}

.p-password-field {
  padding-bottom: 8px;
  width: 100%;
}

.p-password-field__control {
  background: #f0f0f0;
  height: 40px;
  position: relative;
}

.p-password-field__fieldset {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  bottom: 0;
  left: 0;
  margin: 0;
  padding: 0 0 0 8px;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: -6px;
}

.p-password-field__legend {
  font-size: 12px;
  height: 11px;
  line-height: 11px;
  max-width: 0;
  overflow: hidden;
  padding: 0;
  transition: max-width 0.15s ease;
  visibility: hidden;
  white-space: nowrap;
}

.p-password-field--active .p-password-field__legend {
  max-width: 130px;
  padding: 0 4px;
}

.p-password-field__label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 16px;
  left: 12px;
  line-height: 16px;
  pointer-events: none;
  position: absolute;
  top: 12px;
  transform-origin: top left;
  transition:
    color 0.15s ease,
    transform 0.15s ease;
}

.p-password-field--active .p-password-field__label {
  background: #f0f0f0;
  padding: 0 4px;
  transform: translateY(-18px) scale(0.75);
}

.p-password-field__input {
  background: transparent;
  border: 0;
  box-sizing: border-box;
  color: rgba(0, 0, 0, 0.87);
  font-size: 16px;
  height: 40px;
  outline: 0;
  padding: 8px 44px 8px 12px;
  width: 100%;
}

.p-password-field__append {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--v-primary-base, #009688);
  cursor: pointer;
  display: flex;
  font-size: 20px;
  height: 40px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 4px;
  top: 0;
  width: 36px;
}

.p-password-field__control:focus-within .p-password-field__fieldset {
  border-color: var(--v-primary-base, #009688);
  border-width: 2px;
}

.p-password-field__details {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  line-height: 12px;
  min-height: 14px;
  padding: 4px 12px 0;
}

.p-password-field--error .p-password-field__fieldset {
  border-color: #ff5252;
}

.p-password-field--error .p-password-field__details,
.p-password-field--error .p-password-field__label {
  color: #ff5252;
}

@media only screen and (min-width: 600px) {
  #registration-completion {
    .p-btn {
      width: 160px;
    }
  }
}
</style>
